import React from 'react';
import { FaCalendarAlt, FaClock, FaPencilAlt } from 'react-icons/fa';
import { PROFESSIONAL_TYPES } from './resourcesConstants';

const STATUS_BADGE_STYLES = {
  pending: 'bg-yellow-100 text-yellow-700',
  confirmed: 'bg-green-100 text-green-700',
  completed: 'bg-blue-100 text-blue-700',
  cancelled: 'bg-gray-100 text-gray-500'
};

const getStatusBadge = (status) => STATUS_BADGE_STYLES[status] || 'bg-gray-100 text-gray-600';

export function ConsultationsTab({ consultations, onCancel, onEdit }) {
  const pending = consultations.filter(c => c.status === 'pending' || c.status === 'confirmed');
  const past = consultations.filter(c => c.status === 'completed' || c.status === 'cancelled');

  return (
    <div data-testid="consultations-tab">
      <h2 className="text-2xl font-bold text-gray-800 mb-2">My Consultations</h2>
      <p className="text-gray-600 mb-6">Track your consultation requests and history</p>

      {pending.length === 0 && past.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl">
          <FaCalendarAlt className="text-gray-300 text-5xl mx-auto mb-4" />
          <p className="text-gray-600">No consultations yet</p>
          <p className="text-sm text-gray-500 mt-2">Browse professionals to book your first consultation</p>
        </div>
      ) : (
        <>
          {pending.length > 0 && (
            <div className="mb-8">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Upcoming</h3>
              <div className="space-y-4">
                {pending.map((consultation) => {
                  const typeInfo = PROFESSIONAL_TYPES[consultation.professional_type] || { label: 'Professional' };
                  return (
                    <div
                      key={consultation.consultation_id}
                      className="bg-white rounded-xl shadow-sm p-5"
                      data-testid={`consultation-${consultation.consultation_id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-bold text-gray-800">{consultation.professional_name}</h4>
                          <p className="text-sm text-gray-600">{typeInfo.label}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusBadge(consultation.status)}`}>
                          {consultation.status.charAt(0).toUpperCase() + consultation.status.slice(1)}
                        </span>
                      </div>

                      <div className="mt-4 flex items-center space-x-4 text-sm text-gray-600">
                        <span className="flex items-center">
                          <FaCalendarAlt className="mr-2" />
                          {new Date(consultation.preferred_date).toLocaleDateString()}
                        </span>
                        <span className="flex items-center">
                          <FaClock className="mr-2" />
                          {consultation.preferred_time}
                        </span>
                        <span className="capitalize">{consultation.consultation_type.replace('_', ' ')}</span>
                      </div>

                      {consultation.notes && (
                        <p className="mt-3 text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
                          {consultation.notes}
                        </p>
                      )}

                      {(consultation.status === 'pending' || consultation.status === 'confirmed') && (
                        <div className="mt-4 flex items-center space-x-4">
                          <button
                            onClick={() => onEdit(consultation)}
                            className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center"
                            data-testid={`edit-consultation-${consultation.consultation_id}`}
                          >
                            <FaPencilAlt className="mr-1" />
                            Edit
                          </button>
                          <button
                            onClick={() => onCancel(consultation.consultation_id)}
                            className="text-red-600 hover:text-red-700 text-sm font-medium"
                          >
                            Cancel Consultation
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {past.length > 0 && (
            <div>
              <h3 className="text-lg font-bold text-gray-800 mb-4">History</h3>
              <div className="space-y-3">
                {past.map((consultation) => (
                  <div
                    key={consultation.consultation_id}
                    className="bg-white rounded-lg shadow-sm p-4 flex items-center justify-between"
                  >
                    <div>
                      <h4 className="font-medium text-gray-800">{consultation.professional_name}</h4>
                      <p className="text-sm text-gray-500">
                        {new Date(consultation.preferred_date).toLocaleDateString()} • {consultation.consultation_type.replace('_', ' ')}
                      </p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusBadge(consultation.status)}`}>
                      {consultation.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
